import React, { JSX, useEffect, useState } from 'react';
import Icons from 'components/atoms/Icons';
import {
  CLINICAL_TRIALS_STUDY_BASE,
  formatLocationLine,
  formatOverallStatusLabel,
  getPhaseBadgeLabels,
  getStatusBadgeLabel,
  parseEligibilityCriteria,
} from 'lib/clinical-trials/api';
import { getStudyByNctId } from 'lib/clinical-trials/client';
import { ClinicalTrial } from 'lib/clinical-trials/types';
import { ClinicalTrialsDetailsSkeleton } from './ClinicalTrialsSkeletons';

export type ClinicalTrialsDetailsProps = {
  nctId: string;
  onBack: () => void;
  interestUrlTemplate?: string;
};

const HOPE_LINE = '(844) 673-4467';

const buildUrlFromTemplate = (
  template: string | undefined,
  nctId: string,
  fallback: string
): string => {
  if (!template?.trim()) return fallback;
  return template.replace(/\{nctId\}/gi, nctId);
};

const ClinicalTrialsDetails = (
  props: ClinicalTrialsDetailsProps
): JSX.Element => {
  const { nctId, onBack, interestUrlTemplate } = props;
  const [trial, setTrial] = useState<ClinicalTrial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!nctId) {
      setLoading(false);
      setTrial(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    getStudyByNctId(nctId)
      .then(result => {
        if (cancelled) return;
        setTrial(result);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setTrial(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nctId]);

  if (loading) {
    return (
      <div className="clinical-trials-details">
        <button
          type="button"
          className="clinical-trials-details__back"
          onClick={onBack}
        >
          <Icons id="pld-chevron-left" />
          Back to search
        </button>
        <ClinicalTrialsDetailsSkeleton />
      </div>
    );
  }

  if (error || !trial) {
    return (
      <div className="clinical-trials-details">
        <button
          type="button"
          className="clinical-trials-details__back"
          onClick={onBack}
        >
          <Icons id="pld-chevron-left" />
          Back to search
        </button>
        <h1 className="osf-display-h2 clinical-trials-details__title">
          We can&rsquo;t find this clinical trial.
        </h1>
        <p>
          Check the link and try again, or return to search to find another
          study.
        </p>
      </div>
    );
  }

  const heading = trial.officialTitle || trial.briefTitle;
  const statusLabel = getStatusBadgeLabel(trial.overallStatus);
  const phaseLabels = getPhaseBadgeLabels(trial.phases);
  const studyTypeLabel = formatOverallStatusLabel(trial.studyType);
  const interestUrl = buildUrlFromTemplate(
    interestUrlTemplate,
    trial.nctId,
    `${trial.studyUrl || `${CLINICAL_TRIALS_STUDY_BASE}/${trial.nctId}`}#contacts-locations`
  );
  const learnMoreUrl =
    trial.studyUrl || `${CLINICAL_TRIALS_STUDY_BASE}/${trial.nctId}`;
  const locationLines = (trial.locations || [])
    .map(formatLocationLine)
    .filter(Boolean);
  const {
    inclusion,
    exclusion,
    body: eligibilityBody,
  } = parseEligibilityCriteria(trial.eligibilityCriteria);
  const contactEmail = trial.contacts?.find(contact => contact.email)?.email;
  const contactPhone =
    trial.contacts?.find(contact => contact.phone)?.phone || HOPE_LINE;

  return (
    <div className="clinical-trials-details">
      <button
        type="button"
        className="clinical-trials-details__back"
        onClick={onBack}
      >
        <Icons id="pld-chevron-left" />
        Back to search
      </button>

      <div className="clinical-trials-details__header">
        <div className="clinical-trial-result__badges">
          {trial.conditions.map(condition => (
            <span
              key={condition}
              className="clinical-trial-badge clinical-trial-badge--condition"
            >
              {condition}
            </span>
          ))}
          {phaseLabels.map(phase => (
            <span
              key={phase}
              className="clinical-trial-badge clinical-trial-badge--phase"
            >
              {phase}
            </span>
          ))}
          <span
            className={`clinical-trial-badge clinical-trial-badge--status${
              trial.acceptingNewPatients
                ? ' clinical-trial-badge--accepting'
                : ''
            }`}
          >
            {statusLabel}
          </span>
        </div>

        {heading ? (
          <h1 className="osf-display-h2 clinical-trials-details__title">
            {heading}
          </h1>
        ) : null}

        <div className="clinical-trial-result__meta">
          {trial.principalInvestigator ? (
            <p className="clinical-trial-result__meta-row">
              <Icons id="pld-person" className="clinical-trial-result__icon" />
              <span>
                <strong>Principal Investigator:</strong>{' '}
                {trial.principalInvestigator}
              </span>
            </p>
          ) : null}
          {trial.nctId ? (
            <p className="clinical-trial-result__meta-row">
              <Icons
                id="pld-document-search"
                className="clinical-trial-result__icon"
              />
              <span>
                <strong>ClinicalTrials.gov Identifier:</strong>{' '}
                <a
                  href={learnMoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clinical-trial-result__nct-link"
                >
                  {trial.nctId}
                </a>
              </span>
            </p>
          ) : null}
        </div>

        <div className="clinical-trials-details__actions">
          <a
            href={interestUrl}
            className="btn-primary no-icon"
            target="_blank"
            rel="noopener noreferrer"
          >
            I&rsquo;m Interested
          </a>
          <a
            href={learnMoreUrl}
            className="btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn More
          </a>
        </div>
      </div>

      <div className="clinical-trials-details__grid">
        <div className="clinical-trials-details__main">
          <section className="clinical-trials-details__section">
            <h2>Details</h2>
            <dl className="clinical-trials-details__rows">
              {trial.conditions.length ? (
                <div className="clinical-trials-details__row">
                  <dt>Disease Focus</dt>
                  <dd>{trial.conditions.join(', ')}</dd>
                </div>
              ) : null}
              {studyTypeLabel ? (
                <div className="clinical-trials-details__row">
                  <dt>Study Type</dt>
                  <dd>{studyTypeLabel}</dd>
                </div>
              ) : null}
              {locationLines.length ? (
                <div className="clinical-trials-details__row">
                  <dt>Location(s)</dt>
                  <dd>
                    {locationLines.map(line => (
                      <p key={line}>{line}</p>
                    ))}
                  </dd>
                </div>
              ) : null}
              {trial.keywords?.length ? (
                <div className="clinical-trials-details__row clinical-trials-details__row--keywords">
                  <dt>Keywords</dt>
                  <dd>
                    <ul className="clinical-trials-details__keywords">
                      {trial.keywords.map(keyword => (
                        <li key={keyword}>{keyword}</li>
                      ))}
                    </ul>
                  </dd>
                </div>
              ) : null}
            </dl>
          </section>
        </div>

        <aside className="clinical-trials-details__contact">
          <h2 className="osf-display-h3">Contact Us</h2>
          <p>OSF Hope Line · {contactPhone}</p>
          {contactEmail ? (
            <a
              href={`mailto:${contactEmail}`}
              className="btn-tertiary clinical-trials-details__email"
            >
              Email the research team
            </a>
          ) : null}
          <h3 className="osf-display-h3">Interested in this trial?</h3>
          <p className="clinical-trials-details__contact-copy">
            By taking part in a clinical trial you may access treatments not
            available elsewhere and help advance cancer research. Call or email
            to check your eligibility and next steps.
          </p>
        </aside>
      </div>

      {trial.briefSummary ? (
        <section className="clinical-trials-details__section">
          <h2>Study Overview</h2>
          <p className="clinical-trials-details__prose">{trial.briefSummary}</p>
        </section>
      ) : null}

      {inclusion.length || exclusion.length || eligibilityBody ? (
        <section className="clinical-trials-details__section">
          <h2>Eligibility</h2>
          {eligibilityBody ? (
            <p className="clinical-trials-details__prose">{eligibilityBody}</p>
          ) : (
            <div className="clinical-trials-details__eligibility">
              {inclusion.length ? (
                <div>
                  <h3>Inclusion Criteria</h3>
                  <ul>
                    {inclusion.map((item, index) => (
                      <li key={`in-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {exclusion.length ? (
                <div className="clinical-trials-details__criteria--exclusion">
                  <h3>Exclusion Criteria</h3>
                  <ul>
                    {exclusion.map((item, index) => (
                      <li key={`ex-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
};

export default ClinicalTrialsDetails;
